import SearchField from "@/components/SearchField/SearchField";
import { Card, List, Pagination } from "@/components/ui";
import { Button } from "@/components/ui";
import { IconButton } from "@/components/ui";
import { AUTHORITIES } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IUser } from "@/shared/model/user.model";
import { EditDialogType } from "@/shared/util/dialog-utils";
import {
  ITEMS_PER_PAGE,
  PaginationStateWithSearch,
} from "@/shared/util/pagination.constants";
import { RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Translate } from "react-jhipster";
import {
  UserManagementState,
  getUsers,
} from "../../shared/reducers/user-management.reducer";
import TeacherRow from "./components/TeacherRow";
import ViewTeacherMaterial from "./components/ViewTeacherMaterial";

const TeachersSupervision = () => {
  const dispatch = useAppDispatch();
  const [viewMaterialDialog, setViewMaterialDialog] =
    useState<EditDialogType<IUser>>({ open: false, entity: null });
  const [paginationState, setPaginationState] =
    useState<PaginationStateWithSearch>({
      activePage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      sort: "lastName",
      order: "ASC",
      searchValue: "",
    });
  const {
    users: userList,
    loading,
    totalItems,
    updateSuccess,
  } = useAppSelector<UserManagementState>((state) => state.userManagement);

  const getUserList = () => {
    const params = new URLSearchParams();
    params.append("filter.roles.name", "$eq:" + AUTHORITIES.ROLE_TEACHER);
    if (paginationState.searchValue) {
      params.append("search", paginationState.searchValue);
    }
    dispatch(
      getUsers({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        filters: params.toString(),
      })
    );
  };

  useEffect(() => {
    getUserList();
  }, [
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
    paginationState.searchValue,
  ]);

  useEffect(() => {
    if (updateSuccess) {
      getUserList();
    }
  }, [updateSuccess]);

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const searchUsers = (value: string) => {
    setPaginationState((prev) => ({ ...prev, activePage: 1, searchValue: value }));
  };

  return (
    <div className="w-full">
      <Card className="surface grid overflow-hidden rounded-3xl grid-rows-[min-content_auto_min-content] h-full p-0">
        <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30 relative">
          <div className="flex flex-wrap items-center justify-between gap-2 p-4">
            <h2 className="text-heading font-semibold text-[var(--color-mockup-text)] hidden sm:block">
              Controllo attività docenti
            </h2>
            <div className="flex flex-1 items-center gap-2 sm:flex-initial">
              <IconButton
                onClick={getUserList}
                disabled={loading}
                className="lg:hidden"
                aria-label="Aggiorna"
              >
                <RefreshCw className="h-4 w-4" />
              </IconButton>
              <Button
                variant="ghost"
                size="sm"
                onClick={getUserList}
                disabled={loading}
                className="hidden lg:inline-flex"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                <Translate contentKey="userManagement.home.refreshListLabel" />
              </Button>
              <div className="ml-2 min-w-0 flex-1 sm:max-w-xs">
                <SearchField
                  placeholder="Cerca un utente"
                  value={paginationState.searchValue}
                  setSearchValue={searchUsers}
                />
              </div>
            </div>
          </div>
        </div>
        {userList?.length > 0 ? (
          <List className="divide-y">
            {userList.map((user) => (
              <TeacherRow
                key={user.id}
                teacher={user}
                viewTeacher={(teacher) =>
                  setViewMaterialDialog({ open: true, entity: teacher })
                }
              />
            ))}
          </List>
        ) : loading ? (
          <div className="flex justify-center py-8">
            <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="p-4 text-center">
            <p className="text-body text-[var(--color-mockup-text-mid)]">
              <Translate contentKey="userManagement.home.notFound" />
            </p>
          </div>
        )}
        <Pagination
          count={totalItems}
          page={paginationState.activePage - 1}
          rowsPerPage={paginationState.itemsPerPage}
          onPageChange={(p) => handlePagination(p + 1)}
          onRowsPerPageChange={handleRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
          }
        />
      </Card>
      <ViewTeacherMaterial
        teacher={viewMaterialDialog.entity}
        open={viewMaterialDialog.open}
        onClose={() => setViewMaterialDialog({ open: false, entity: null })}
      />
    </div>
  );
};

export default TeachersSupervision;
